import React, { createContext, useContext, useRef, ReactNode, useEffect } from 'react';
import PilotDrawer, { PilotDrawerRef } from '../components/PilotDrawer';

interface PilotDrawerContextType {
    openWithPrompt: (prompt: string) => void;
    openWithoutPrompt: () => void;
    toggle: () => void;
}

const PilotDrawerContext = createContext<PilotDrawerContextType | null>(null);

interface PilotDrawerProviderProps {
    children: ReactNode;
}

export const PilotDrawerProvider: React.FC<PilotDrawerProviderProps> = ({ children }) => {
    const pilotDrawerRef = useRef<PilotDrawerRef>(null);

    const openWithPrompt = (prompt: string) => {
        pilotDrawerRef.current?.openWithPrompt(prompt);
    };

    const openWithoutPrompt = () => {
        pilotDrawerRef.current?.openWithoutPrompt();
    };

    const toggle = () => {
        pilotDrawerRef.current?.toggle();
    };

    // Add keyboard shortcut listener
    useEffect(() => {
        const handleKeyDown = (event: KeyboardEvent) => {
            // Check for Ctrl+P (Windows/Linux) or Cmd+P (Mac)
            if ((event.ctrlKey || event.metaKey) && event.key === 'p') {
                event.preventDefault(); // Prevent browser's default print dialog
                toggle(); // Use toggle instead of openWithoutPrompt
            }
        };

        document.addEventListener('keydown', handleKeyDown);

        return () => {
            document.removeEventListener('keydown', handleKeyDown);
        };
    }, []);

    return (
        <PilotDrawerContext.Provider value={{ openWithPrompt, openWithoutPrompt, toggle }}>
            {children}
            <PilotDrawer ref={pilotDrawerRef} startOpen={false} />
        </PilotDrawerContext.Provider>
    );
};

export const usePilotDrawer = () => {
    const context = useContext(PilotDrawerContext);
    if (!context) {
        throw new Error('usePilotDrawer must be used within a PilotDrawerProvider');
    }
    return context;
};
